import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useTheme } from '../contexts/ThemeContext';
import { Mail, Send, MessageCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

const FaleConoscoPage: React.FC = () => {
  const { theme } = useTheme();
  const [emailForm, setEmailForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEmailForm(prev => ({ ...prev, [name]: value }));
  };

  const whatsappNumber = '5524999814872'; // Número formatado para link internacional
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Olá! Gostaria de entrar em contato sobre o Boardverso.')}`;
  const emailTo = 'contato@boardverso.com';

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const mailtoLink = `mailto:${emailTo}?subject=${encodeURIComponent(emailForm.subject)}&body=${encodeURIComponent(`Nome: ${emailForm.name}\nEmail: ${emailForm.email}\n\nMensagem:\n${emailForm.message}`)}`;
    window.location.href = mailtoLink;
    toast.success('Seu aplicativo de e-mail será aberto para enviar a mensagem!');
  };
  

  return (
    <>
      <SEO
        title="Fale Conosco"
        description="Entre em contato com a equipe do Boardverso. Tire suas dúvidas, envie sugestões ou reporte problemas."
      />
      <div className={`container mx-auto max-w-3xl px-4 py-12 ${theme === 'dark' ? 'text-gray-100' : 'text-gray-800'}`}>
        <h1 className="text-3xl md:text-4xl font-bold text-center mb-8">Fale Conosco</h1>
        <p className="text-center mb-12 text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}">
          Tem alguma dúvida, sugestão, ou precisa de ajuda? Escolha uma das opções abaixo para entrar em contato conosco.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Card WhatsApp */}
          <Card className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white'}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="h-6 w-6 text-green-500" />
                WhatsApp
              </CardTitle>
              <CardDescription className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Envie uma mensagem diretamente para nosso WhatsApp. Ideal para respostas rápidas.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="mb-2">Número: <strong>(24) 99981-4872</strong></p>
              <p className="text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}">
                Clique no botão abaixo para abrir o WhatsApp já com uma mensagem padrão.
              </p>
            </CardContent>
            <CardFooter>
              <Button asChild className="w-full bg-green-500 hover:bg-green-600 text-white">
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2">
                  <MessageCircle className="h-5 w-5" />
                  Iniciar Conversa
                </a>
              </Button>
            </CardFooter>
          </Card>

          {/* Card E-mail */}
          <Card className={`${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white'}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-6 w-6 text-blue-500" />
                E-mail
              </CardTitle>
              <CardDescription className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Prefere enviar um e-mail? Preencha o formulário abaixo e clique em enviar.
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleEmailSubmit}>
              <CardContent className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-1">Seu Nome</label>
                  <Input type="text" id="name" name="name" value={emailForm.name} onChange={handleInputChange} required 
                         className={`${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : ''}`}/>
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-1">Seu E-mail</label>
                  <Input type="email" id="email" name="email" value={emailForm.email} onChange={handleInputChange} required 
                         className={`${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : ''}`}/>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium mb-1">Assunto</label>
                  <Input type="text" id="subject" name="subject" value={emailForm.subject} onChange={handleInputChange} required 
                         className={`${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : ''}`}/>
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium mb-1">Sua Mensagem</label>
                  <Textarea id="message" name="message" value={emailForm.message} onChange={handleInputChange} rows={4} required 
                            className={`${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : ''}`}/>
                </div>
              </CardContent>
              <CardFooter>
                <Button type="submit" className="w-full bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center gap-2">
                  <Send className="h-5 w-5" />
                  Enviar E-mail
                </Button>
              </CardFooter>
            </form>
          </Card>
        </div>
        
        <div className="mt-12 text-center">
          <p className={`mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Nosso horário de atendimento é de Segunda a Sexta, das 9h às 18h.
          </p>
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
            Responderemos o mais breve possível!
          </p>
        </div>

      </div>
    </>
  );
};

export default FaleConoscoPage; 